import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { supabase, supabaseConfig, isSupabaseConfigured } from '../../lib/supabase';

// ---------------------------------------------------------------------------
// Local-only preferences.
//
// The four toggles below are stored in localStorage and read on mount. They
// don't try to sync across devices or across teachers — that's what a real
// per-user preferences table would do, and we haven't built one yet.
//
// The section that lives above the toggles ("Account & sync status") is
// read-only: current live/demo mode, current signed-in user, current
// Supabase project. It's the answer to the question users actually ask when
// they open Preferences: "why am I in demo mode?" / "who am I signed in
// as?" / "how do I sign out?"
// ---------------------------------------------------------------------------

const PREFS_STORAGE_KEY = 'edutracker.prefs.v1';

const DEFAULT_PREFS = {
  compactDensity: false,
  showFunctionHintsOnIncidents: true,
  askBeforeDeletingIncidents: true,
  autoCollapseSidebarOnMobile: true,
};

function loadPrefs() {
  try {
    if (typeof window === 'undefined') return { ...DEFAULT_PREFS };
    const raw = window.localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs(prefs) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore — private-mode Safari, storage full, etc.
  }
}

const PreferencesPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const [savedAt, setSavedAt] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(null);

  const supabaseLive = isSupabaseConfigured();

  useEffect(() => {
    savePrefs(prefs);
    setSavedAt(new Date());
  }, [prefs]);

  useEffect(() => {
    let cancelled = false;
    async function fetchUser() {
      if (!supabase) return;
      try {
        const { data } = await supabase.auth.getUser();
        if (!cancelled) setCurrentUser(data?.user || null);
      } catch {
        if (!cancelled) setCurrentUser(null);
      }
    }
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePref = (key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetPrefs = () => {
    setPrefs({ ...DEFAULT_PREFS });
  };

  const handleSignOut = async () => {
    setSignOutError(null);
    setSigningOut(true);
    try {
      if (supabase) {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      }
      // clear demo/local data caches on sign-out so the next user doesn't
      // inherit them. The dedicated app data (incidents, plans, etc.) lives
      // under context-specific keys; we don't wipe those here, only the
      // auth-adjacent bits.
      try {
        window.localStorage.removeItem('supabase.auth.token');
      } catch {
        // ignore
      }
      navigate('/login');
    } catch (err) {
      setSignOutError(err?.message || 'Sign out failed. Please try again.');
    } finally {
      setSigningOut(false);
    }
  };

  // Never surface the real Supabase project hostname to a demo user
  // — it's the same "here's how the sausage is made" leak that the
  // /go-live lockdown blocks. A demo visitor tapping Preferences
  // would otherwise see e.g. `mbtyctdhueabxmvlpj1p.supabase.co`,
  // which is the project reference an attacker (or a competitor
  // spinning up their own copy) would want to grab.
  const isRealAuthedUser = currentUser?.backend === 'supabase';
  const projectHost = (() => {
    if (!isRealAuthedUser) return null;
    try {
      if (!supabaseConfig?.url) return null;
      return new URL(supabaseConfig.url).hostname;
    } catch {
      return null;
    }
  })();

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16`}
      >
        <div className="p-6 max-w-4xl mx-auto space-y-8">
          <Breadcrumb
            customItems={[
              { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
              { label: 'Preferences', path: null, icon: 'Settings', current: true },
            ]}
          />

          <div>
            <h1 className="text-3xl font-bold text-foreground">Preferences</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Personal settings for how EduTracker Pro behaves on this device, plus your
              current account and sync status.
            </p>
          </div>

          {/* Account & sync status ------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ShieldCheck" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                Account & sync status
              </h2>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">
                  Data sync
                </dt>
                <dd className="flex items-center gap-2">
                  {supabaseLive ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-success" />
                      <span className="text-sm font-medium text-foreground">Live sync</span>
                      <span className="text-xs text-muted-foreground">
                        (saving to Supabase)
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 rounded-full bg-warning" />
                      <span className="text-sm font-medium text-foreground">Demo mode</span>
                      <span className="text-xs text-muted-foreground">
                        (data lives only in this browser)
                      </span>
                    </>
                  )}
                </dd>
              </div>

              {isRealAuthedUser && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">
                    Database
                  </dt>
                  <dd className="text-sm text-foreground truncate">
                    {projectHost || (
                      <span className="text-muted-foreground italic">
                        not configured
                      </span>
                    )}
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">
                  Signed in as
                </dt>
                <dd className="text-sm text-foreground truncate">
                  {currentUser?.email ? (
                    currentUser.email
                  ) : supabaseLive ? (
                    <span className="text-muted-foreground italic">not signed in</span>
                  ) : (
                    <span className="text-muted-foreground italic">
                      demo user (no cloud account)
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">
                  App version
                </dt>
                <dd className="text-sm text-foreground">2.1.0</dd>
              </div>
            </dl>

            {!supabaseLive && (
              <div className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm">
                <div className="flex items-start gap-2">
                  <Icon
                    name="Info"
                    size={16}
                    className="text-blue-600 flex-shrink-0 mt-0.5"
                  />
                  <div>
                    {/* Teacher-facing copy — same rewrite the demo
                        banner got. No env-var names, no "Netlify",
                        no "redeploy". This panel is visible to demo
                        users so it must not leak setup mechanics. */}
                    <p className="text-blue-900">
                      You're in <strong>Demo mode</strong>. Anything you
                      enter is stored in this browser only — it doesn't
                      sync to a real database and your team can't see
                      it. Ready to use this with your caseload?
                    </p>
                    <p className="mt-2">
                      <button
                        type="button"
                        onClick={() => navigate('/login?live=1')}
                        className="text-xs font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                      >
                        Sign in with a real account →
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/help')}
              >
                <Icon name="HelpCircle" size={14} />
                Help & Manual
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                disabled={signingOut}
              >
                <Icon name="LogOut" size={14} />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </Button>
            </div>
            {signOutError && (
              <p className="text-xs text-destructive mt-2" role="alert">
                {signOutError}
              </p>
            )}
          </section>

          {/* App preferences ------------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Sliders" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">App preferences</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              These settings only affect <strong>this device / browser</strong>. They save
              automatically as you toggle them.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={prefs.compactDensity}
                  onChange={() => togglePref('compactDensity')}
                />
                <div className="flex-1 -mt-0.5">
                  <p className="text-sm font-medium text-foreground">Compact density</p>
                  <p className="text-xs text-muted-foreground">
                    Reduce padding across lists and tables so more rows fit on screen. Nice
                    on desktop, may feel cramped on phones.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={prefs.showFunctionHintsOnIncidents}
                  onChange={() => togglePref('showFunctionHintsOnIncidents')}
                />
                <div className="flex-1 -mt-0.5">
                  <p className="text-sm font-medium text-foreground">
                    Show function-of-behavior hints while logging incidents
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Displays inline reminders about the six functions (attention, escape,
                    tangible, sensory, automatic, unknown) as you fill out an incident.
                    Uncheck once you know them cold.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={prefs.askBeforeDeletingIncidents}
                  onChange={() => togglePref('askBeforeDeletingIncidents')}
                />
                <div className="flex-1 -mt-0.5">
                  <p className="text-sm font-medium text-foreground">
                    Confirm before deleting incidents or plans
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Adds a confirmation prompt to destructive actions. Recommended.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={prefs.autoCollapseSidebarOnMobile}
                  onChange={() => togglePref('autoCollapseSidebarOnMobile')}
                />
                <div className="flex-1 -mt-0.5">
                  <p className="text-sm font-medium text-foreground">
                    Auto-collapse the sidebar on phone/tablet
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Closes the sidebar automatically after you tap a page. Reopen with the{' '}
                    <span className="font-mono">☰</span> icon in the top-left.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-muted-foreground">
                {savedAt
                  ? `Saved locally at ${savedAt.toLocaleTimeString()}`
                  : 'Preferences will save as you toggle them.'}
              </p>
              <Button variant="ghost" size="sm" onClick={resetPrefs}>
                <Icon name="RotateCcw" size={14} />
                Reset to defaults
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mt-6 pt-4 border-t border-border">
              Note: EduTracker Pro doesn't yet sync preferences across your devices. If you
              log in from a different browser or phone, you'll see the default settings
              until you toggle them again there.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PreferencesPage;
